import { Suspense, lazy, useCallback, useRef, useState } from 'react'
import type { MouseEvent, ReactElement } from 'react'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import GridViewIcon from '@mui/icons-material/GridView'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ListAltIcon from '@mui/icons-material/ListAlt'
import MenuIcon from '@mui/icons-material/Menu'
import PersonIcon from '@mui/icons-material/Person'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import StraightenIcon from '@mui/icons-material/Straighten'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography
} from '@mui/material'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import AppFooter from './components/AppFooter'
import AppNavDrawer from './components/appShell/AppNavDrawer'
import type { AppNavItem } from './components/appShell/AppNavDrawer'
import AppUserMenu from './components/appShell/AppUserMenu'
import UserAvatar from './components/appShell/UserAvatar'
import NotificationBell from './components/notifications/NotificationBell'
import NotificationsDrawer from './components/notifications/NotificationsDrawer'
import NotificationToastStack from './components/notifications/NotificationToastStack'
import TimedAlertSnackbar from './components/TimedAlertSnackbar'
import LazyRouteErrorBoundary from './components/LazyRouteErrorBoundary'
import { AppFooterProvider } from './contexts/AppFooterContext'
import { useAuthContext } from './contexts/AuthContext'
import { useRequestsContext } from './contexts/RequestsContext'
import { useNotificationsCenter } from './hooks/useNotificationsCenter'
import { useIsBelowDesktopLayout, useIsPhoneLayout } from './hooks/useViewport'
import { stripRequestDetailsView } from './utils/requestDetailsView'
import { getUserInitials } from './utils/userInitials'
import logoImage from './assets/svetobur-logo.png'
import {
  RequireAdmin,
  RequireAnonymous,
  RequireAuth,
  RequireCatalogAccess,
  RequireMetrologyAccess,
  RequirePasswordChangeResolved,
  RequireWarehouseAccess
} from './routes/RouteGuards'

const AuthPage = lazy(() => import('./pages/AuthPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const CreateRequestPage = lazy(() => import('./pages/CreateRequestPage'))
const RequestsListPage = lazy(() => import('./pages/RequestsListPage'))
const InvoicesRegistryPage = lazy(() => import('./pages/InvoicesRegistryPage'))
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'))
const AdminPage = lazy(() => import('./pages/AdminPage'))
const CatalogPage = lazy(() => import('./pages/CatalogPage'))
const WarehouseStockPage = lazy(() => import('./pages/WarehouseStockPage'))
const WarehouseIssueRequestsPage = lazy(() => import('./pages/WarehouseIssueRequestsPage'))
const MetrologyPage = lazy(() => import('./pages/MetrologyPage'))

function RouteLoadingFallback({ message }: { message: string }) {
  return <TimedAlertSnackbar severity="info">{message}</TimedAlertSnackbar>
}

const APP_SECTIONS = {
  requests: { key: 'requests', path: '/requests', label: 'Заявки', icon: ListAltIcon },
  invoices: { key: 'invoices', path: '/invoices', label: 'Счета', icon: ReceiptLongIcon },
  warehouse: { key: 'warehouse', path: '/warehouse', label: 'Склад', icon: WarehouseIcon },
  catalog: { key: 'catalog', path: '/catalog', label: 'Каталог', icon: GridViewIcon },
  metrology: { key: 'metrology', path: '/metrology', label: 'Мерители', icon: StraightenIcon },
  feedback: { key: 'feedback', path: '/feedback', label: 'Обратная связь', icon: ChatBubbleOutlineIcon },
  profile: { key: 'profile', path: '/profile', label: 'Профиль', icon: PersonIcon },
  admin: { key: 'admin', path: '/admin', label: 'Админка', icon: AdminPanelSettingsIcon }
} satisfies Record<string, AppNavItem>

function isPathInSection(pathname: string, sectionPath: string): boolean {
  return pathname === sectionPath || pathname.startsWith(`${sectionPath}/`)
}

function findActiveSection(pathname: string): AppNavItem | undefined {
  return Object.values(APP_SECTIONS).find((section) => isPathInSection(pathname, section.path))
}

function parseSelectedRequestIdFromPath(pathname: string): number | null | 'invalid' {
  if (pathname === '/requests') {
    return null
  }

  const match = /^\/requests\/([^/]+)$/.exec(pathname)
  if (!match) {
    return 'invalid'
  }

  const parsedRequestId = Number(match[1])
  if (!Number.isInteger(parsedRequestId) || parsedRequestId <= 0) {
    return 'invalid'
  }

  return parsedRequestId
}

function AuthenticatedShell() {
  const navigate = useNavigate()
  const location = useLocation()
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
  const isPhone = useIsPhoneLayout()
  const isBelowDesktop = useIsBelowDesktopLayout()

  const {
    apiBaseUrl,
    authUser,
    authUsername,
    hasPermission,
    requirePasswordChange,
    openProfilePage,
    handleLogout
  } = useAuthContext()

  const { clearStatus } = useRequestsContext()

  // The notifications API needs a resolved password change, like the rest of the work area.
  const canUseNotifications = !requirePasswordChange
  const closeNotifications = useCallback(() => {
    setIsNotificationsOpen(false)
  }, [])
  const navigateToNotification = useCallback((path: string) => {
    clearStatus()
    navigate(path)
  }, [clearStatus, navigate])
  // One bell is rendered at a time (phone, tablet or desktop header); a closed toast returns the focus to it.
  const notificationBellRef = useRef<HTMLButtonElement>(null)
  const notifications = useNotificationsCenter({
    apiBaseUrl,
    enabled: canUseNotifications,
    isOpen: isNotificationsOpen,
    onClose: closeNotifications,
    onNavigate: navigateToNotification
  })

  const isUserMenuOpen = Boolean(userMenuAnchorEl)

  const canAccessCatalog = hasPermission('NomenclatureManage')
  const canAccessWarehouse = hasPermission('WarehouseViewStock')
  const canAccessMetrology = hasPermission('MetrologyView')
  const canAccessAdminPanel = hasPermission('SystemAdmin')
  const navigationItems: AppNavItem[] = [
    APP_SECTIONS.requests,
    APP_SECTIONS.invoices,
    ...(canAccessWarehouse ? [APP_SECTIONS.warehouse] : []),
    ...(canAccessCatalog ? [APP_SECTIONS.catalog] : []),
    ...(canAccessMetrology ? [APP_SECTIONS.metrology] : [])
  ]
  const activeNavTabIndex = navigationItems.findIndex((item) => isPathInSection(location.pathname, item.path))
  const activeNavTab = activeNavTabIndex >= 0 ? activeNavTabIndex : false
  const activeSection = findActiveSection(location.pathname)
  const isFeedbackActive = location.pathname.startsWith('/feedback')
  const userDisplayName = authUser || 'Пользователь'
  const userInitials = getUserInitials(authUser, authUsername)
  const isCreateRequestRoute = location.pathname === '/requests/new'
  const isRequestsListRoute = location.pathname === '/requests' || /^\/requests\/[^/]+$/.test(location.pathname)
  const selectedRequestId = isRequestsListRoute ? parseSelectedRequestIdFromPath(location.pathname) : null

  function handleNavTabChange(_: React.SyntheticEvent, newValue: number) {
    clearStatus()
    const nextTab = navigationItems[newValue]
    if (nextTab) {
      navigate(nextTab.path)
    }
  }

  function openAdminPanel() {
    if (requirePasswordChange || !canAccessAdminPanel) return
    clearStatus()
    navigate('/admin')
  }

  async function handleLogoutClick() {
    clearStatus()
    await handleLogout()
  }

  function handleUserMenuOpen(event: MouseEvent<HTMLElement>) {
    setUserMenuAnchorEl(event.currentTarget)
  }

  function handleUserMenuClose() {
    setUserMenuAnchorEl(null)
  }

  function openFeedbackPage() {
    clearStatus()
    navigate('/feedback')
  }

  function openNotifications() {
    setIsNotificationsOpen(true)
  }

  function renderNotificationBell(sx?: { ml: number }) {
    if (!canUseNotifications) return null
    return (
      <NotificationBell
        ref={notificationBellRef}
        count={notifications.unreadCount}
        open={isNotificationsOpen}
        onClick={openNotifications}
        sx={sx}
      />
    )
  }

  function closeNavDrawer() {
    setIsNavDrawerOpen(false)
  }

  function handleNavDrawerNavigate() {
    clearStatus()
    closeNavDrawer()
  }

  function handleNavDrawerLogoClick() {
    closeNavDrawer()
    navigate('/requests')
  }

  function runAfterNavDrawerClose(action: () => void) {
    return () => {
      closeNavDrawer()
      action()
    }
  }

  function handleRequestSelectionRouteChange(requestId: number | null): void {
    const normalizedRequestId = typeof requestId === 'number' ? requestId : null
    if (normalizedRequestId === null || !Number.isInteger(normalizedRequestId) || normalizedRequestId <= 0) {
      if (location.pathname !== '/requests') {
        navigate({ pathname: '/requests', search: stripRequestDetailsView(location.search) }, { state: location.state })
      }
      return
    }

    const targetPath = `/requests/${normalizedRequestId}`
    if (location.pathname !== targetPath) {
      navigate({ pathname: targetPath, search: stripRequestDetailsView(location.search) }, { state: location.state })
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky" color="inherit" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        {isPhone ? (
          <Toolbar sx={{ px: 1, minHeight: 56, height: 56 }}>
            <IconButton
              aria-label="Открыть меню"
              aria-haspopup="dialog"
              aria-expanded={isNavDrawerOpen}
              onClick={() => setIsNavDrawerOpen(true)}
              sx={{ width: 44, height: 44, borderRadius: 1, flexShrink: 0 }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              src={logoImage}
              alt="Светобур"
              onClick={() => navigate('/requests')}
              sx={{ width: 80, objectFit: 'contain', cursor: 'pointer', mx: 1, flexShrink: 0 }}
            />
            <Typography
              component="span"
              noWrap
              title={activeSection?.label ?? ''}
              sx={{ flex: 1, minWidth: 0, pl: 0.5, fontSize: 15, fontWeight: 600, color: 'text.secondary' }}
            >
              {activeSection?.label ?? ''}
            </Typography>
            {renderNotificationBell()}
            <IconButton
              aria-label={`Меню пользователя: ${userDisplayName}`}
              aria-haspopup="true"
              aria-expanded={isUserMenuOpen}
              onClick={handleUserMenuOpen}
              sx={{ width: 44, height: 44, p: 0, flexShrink: 0 }}
            >
              <UserAvatar initials={userInitials} size={32} />
            </IconButton>
          </Toolbar>
        ) : (
          <Toolbar sx={{ px: { xs: 2, md: 3 }, minHeight: 68 }}>
            <Box
              component="img"
              src={logoImage}
              alt="Светобур"
              onClick={() => navigate('/requests')}
              sx={{ width: 96, objectFit: 'contain', cursor: 'pointer', mr: 2, flexShrink: 0 }}
            />

            <Tabs
              value={activeNavTab}
              onChange={handleNavTabChange}
              textColor="inherit"
              {...(isBelowDesktop ? { variant: 'scrollable', scrollButtons: false } as const : {})}
              sx={{
                flexGrow: 1,
                minWidth: 0,
                minHeight: 68,
                '& .MuiTab-root': { minHeight: 68, textTransform: 'none', fontWeight: 500, fontSize: '0.95rem' },
                '& .MuiTabs-indicator': { backgroundColor: 'primary.main', height: 3 }
              }}
            >
              {navigationItems.map((item) => (
                <Tab key={item.key} label={item.label} />
              ))}
            </Tabs>

            {isBelowDesktop ? (
              <>
                {renderNotificationBell({ ml: 1 })}
                <IconButton
                  aria-label={`Меню пользователя: ${userDisplayName}`}
                  aria-haspopup="true"
                  aria-expanded={isUserMenuOpen}
                  onClick={handleUserMenuOpen}
                  sx={{ width: 44, height: 44, p: 0, ml: 1, flexShrink: 0 }}
                >
                  <UserAvatar initials={userInitials} size={36} />
                </IconButton>
              </>
            ) : (
              <>
                <Tabs
                  value={isFeedbackActive ? 0 : false}
                  textColor="inherit"
                  sx={{
                    minHeight: 68,
                    mr: 1,
                    '& .MuiTab-root': { minHeight: 68, textTransform: 'none', fontWeight: 500, fontSize: '0.95rem' },
                    '& .MuiTabs-indicator': { backgroundColor: 'primary.main', height: 3 }
                  }}
                >
                  <Tab label="Обратная связь" onClick={openFeedbackPage} />
                </Tabs>

                {renderNotificationBell({ ml: 1 })}

                <Button
                  color="inherit"
                  onClick={handleUserMenuOpen}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{ textTransform: 'none', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {userDisplayName}
                </Button>
              </>
            )}
          </Toolbar>
        )}
        <AppUserMenu
          anchorEl={userMenuAnchorEl}
          open={isUserMenuOpen}
          onClose={handleUserMenuClose}
          displayName={userDisplayName}
          username={authUsername}
          showAdmin={canAccessAdminPanel}
          showFeedback={isBelowDesktop}
          onProfile={openProfilePage}
          onAdmin={openAdminPanel}
          onFeedback={openFeedbackPage}
          onLogout={handleLogoutClick}
        />
      </AppBar>

      {isPhone && (
        <AppNavDrawer
          open={isNavDrawerOpen}
          onClose={closeNavDrawer}
          sectionItems={navigationItems}
          secondaryItems={[APP_SECTIONS.feedback]}
          activeKey={activeSection?.key ?? null}
          onNavigate={handleNavDrawerNavigate}
          onLogoClick={handleNavDrawerLogoClick}
          displayName={userDisplayName}
          username={authUsername}
          initials={userInitials}
          showAdmin={canAccessAdminPanel}
          onProfile={runAfterNavDrawerClose(openProfilePage)}
          onAdmin={runAfterNavDrawerClose(openAdminPanel)}
          onLogout={runAfterNavDrawerClose(handleLogoutClick)}
          onNotifications={canUseNotifications ? runAfterNavDrawerClose(openNotifications) : undefined}
          notificationsUnreadCount={notifications.unreadCount}
        />
      )}

      {canUseNotifications && (
        <>
          <NotificationsDrawer {...notifications.drawerProps} />
          <NotificationToastStack {...notifications.toastStackProps} returnFocusRef={notificationBellRef} />
        </>
      )}

      <Container maxWidth="xl" sx={{ py: 3, flex: 1 }}>
        <Stack spacing={2}>
          {requirePasswordChange && (
            <TimedAlertSnackbar severity="warning">
              Для доступа к системе сначала смените пароль в профиле.
            </TimedAlertSnackbar>
          )}

          {isCreateRequestRoute ? (
            <RequirePasswordChangeResolved>
              <Suspense fallback={<RouteLoadingFallback message="Загрузка страницы создания заявки..." />}>
                <CreateRequestPage />
              </Suspense>
            </RequirePasswordChangeResolved>
          ) : isRequestsListRoute ? (
            selectedRequestId === 'invalid' ? (
              <Navigate to="/" replace />
            ) : (
              <RequirePasswordChangeResolved>
                <Suspense fallback={<RouteLoadingFallback message="Загрузка списка заявок..." />}>
                  <RequestsListPage
                    initialSelectedRequestId={selectedRequestId}
                    onSelectedRequestChange={handleRequestSelectionRouteChange}
                  />
                </Suspense>
              </RequirePasswordChangeResolved>
            )
          ) : (
            <Routes>
              <Route element={<RequirePasswordChangeResolved />}>
                <Route path="/" element={<Navigate to="/requests" replace />} />
                <Route
                  path="/invoices"
                  element={(
                    <Suspense fallback={<RouteLoadingFallback message="Загрузка реестра счетов..." />}>
                      <InvoicesRegistryPage />
                    </Suspense>
                  )}
                />
                <Route
                  path="/feedback"
                  element={(
                    <Suspense fallback={<RouteLoadingFallback message="Загрузка страницы обратной связи..." />}>
                      <FeedbackPage />
                    </Suspense>
                  )}
                />
                <Route path="/nomenclature" element={<Navigate to="/catalog" replace />} />
                <Route element={<RequireCatalogAccess />}>
                  <Route
                    path="/catalog"
                    element={(
                      <LazyRouteErrorBoundary
                        title="Не удалось открыть каталог"
                        retryButtonLabel="Повторить загрузку"
                      >
                        <Suspense fallback={<TimedAlertSnackbar severity="info">Загрузка каталога...</TimedAlertSnackbar>}>
                          <CatalogPage />
                        </Suspense>
                      </LazyRouteErrorBoundary>
                    )}
                  />
                </Route>
                <Route element={<RequireWarehouseAccess />}>
                  <Route
                    path="/warehouse"
                    element={(
                      <LazyRouteErrorBoundary
                        title="Не удалось открыть страницу склада"
                        retryButtonLabel="Повторить загрузку"
                      >
                        <Suspense fallback={<TimedAlertSnackbar severity="info">Загрузка остатков склада...</TimedAlertSnackbar>}>
                          <WarehouseStockPage />
                        </Suspense>
                      </LazyRouteErrorBoundary>
                    )}
                  />
                  <Route
                    path="/warehouse/issue-requests"
                    element={(
                      <LazyRouteErrorBoundary
                        title="Не удалось открыть заявки на выдачу"
                        retryButtonLabel="Повторить загрузку"
                      >
                        <Suspense fallback={<TimedAlertSnackbar severity="info">Загрузка заявок на выдачу...</TimedAlertSnackbar>}>
                          <WarehouseIssueRequestsPage />
                        </Suspense>
                      </LazyRouteErrorBoundary>
                    )}
                  />
                </Route>
                <Route element={<RequireMetrologyAccess />}>
                  <Route
                    path="/metrology/*"
                    element={(
                      <LazyRouteErrorBoundary
                        title="Не удалось открыть мерительный инструмент"
                        retryButtonLabel="Повторить загрузку"
                      >
                        <Suspense fallback={<TimedAlertSnackbar severity="info">Загрузка мерительного инструмента...</TimedAlertSnackbar>}>
                          <MetrologyPage />
                        </Suspense>
                      </LazyRouteErrorBoundary>
                    )}
                  />
                </Route>
                <Route element={<RequireAdmin />}>
                  <Route
                    path="/admin"
                    element={(
                      <LazyRouteErrorBoundary
                        title="Не удалось открыть админский интерфейс"
                        retryButtonLabel="Повторить загрузку"
                      >
                        <Suspense fallback={<TimedAlertSnackbar severity="info">Загрузка админского интерфейса...</TimedAlertSnackbar>}>
                          <AdminPage />
                        </Suspense>
                      </LazyRouteErrorBoundary>
                    )}
                  />
                </Route>
              </Route>
              <Route
                path="/profile"
                element={(
                  <Suspense fallback={<RouteLoadingFallback message="Загрузка профиля..." />}>
                    <ProfilePage />
                  </Suspense>
                )}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
        </Stack>
      </Container>
      <AppFooter />
    </Box>
  )
}

export default function App(): ReactElement {
  const { isAuthenticated, legacyResetUrl } = useAuthContext()

  return (
    <Routes>
      <Route element={<RequireAnonymous />}>
        <Route
          path="/login"
          element={(
            <Suspense fallback={<RouteLoadingFallback message="Загрузка страницы входа..." />}>
              <AuthPage />
            </Suspense>
          )}
        />
        <Route
          path="/reset-password"
          element={(
            <Suspense fallback={<RouteLoadingFallback message="Загрузка страницы восстановления пароля..." />}>
              <AuthPage />
            </Suspense>
          )}
        />
      </Route>

      <Route element={<RequireAuth />}>
        <Route
          path="/*"
          element={(
            <AppFooterProvider>
              <AuthenticatedShell />
            </AppFooterProvider>
          )}
        />
      </Route>

      <Route path="*" element={<Navigate to={isAuthenticated ? '/' : legacyResetUrl} replace />} />
    </Routes>
  )
}
